import { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import Navbar from '@/components/layout/Navbar'

const HERO_BG_IMAGE = '/assets/hero-background.png'

const STATS = [
  { label: 'Harvest', value: 'Optimise' },
  { label: 'Grow', value: 'Expand' },
  { label: 'Resource', value: 'Scale' },
  { label: 'Supply Chain', value: 'Global' },
] as const

export default function StrategyHero() {
  const [mobileIndex, setMobileIndex] = useState(0)

  useEffect(() => {
    const startInterval = () => {
      if (window.innerWidth >= 640) return null
      return setInterval(() => {
        setMobileIndex((i) => (i + 1) % STATS.length)
      }, 3000)
    }
    let id = startInterval()
    const onResize = () => {
      if (id) clearInterval(id)
      id = startInterval()
    }
    window.addEventListener('resize', onResize)
    return () => {
      if (id) clearInterval(id)
      window.removeEventListener('resize', onResize)
    }
  }, [])
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  })
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.55, 0.65, 0.75])

  return (
    <section ref={ref} className="relative min-h-[100dvh] w-full overflow-hidden flex flex-col">
      <Navbar heroLayout />
      <div
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-fixed"
        style={{ backgroundImage: `url(${HERO_BG_IMAGE})` }}
      />
      <motion.div
        className="absolute inset-0 z-[1] bg-slate-900"
        style={{ opacity: overlayOpacity }}
      />
      <div className="absolute inset-0 z-[2] bg-gradient-to-b from-slate-900/25 via-transparent to-slate-900/70" />

      <div className="flex-1 flex flex-col justify-center items-center relative z-10 pt-32 pb-20 md:pt-24 md:pb-24 md:py-32 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-[85rem] text-center"
        >
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl font-extrabold text-white/95 leading-[1.1] tracking-tight mb-12 md:mb-16">
            Towards 2030 Strategy
          </h1>
          <p className="font-heading text-xl md:text-2xl lg:text-3xl font-medium text-white/90 leading-relaxed mb-14 md:mb-16">
            Harvest our investments. Grow our footprint. Lead the outside China rare earths industry.
          </p>
          <div className="flex items-center justify-center">
            <a
              href="#strategy-content"
              className="inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
            >
              Explore the strategy
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="relative z-10 border-t border-white/15 bg-black/5 backdrop-blur-[3px]"
      >
        <div className="container py-4 sm:py-8 md:py-10">
          <div className="sm:hidden relative min-h-[52px] flex items-center justify-center overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={mobileIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex flex-col items-center justify-center"
              >
                <div className="text-[10px] font-semibold text-white/50 uppercase tracking-[0.12em]">
                  {STATS[mobileIndex].label}
                </div>
                <div className="text-base font-bold font-heading text-white mt-0.5">
                  {STATS[mobileIndex].value}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="hidden sm:flex flex-row max-w-4xl mx-auto divide-x divide-white/15">
            {STATS.map((stat) => (
              <div
                key={stat.label}
                className="flex-1 flex flex-col items-center justify-center px-8"
              >
                <div className="text-[11px] font-semibold text-white/50 uppercase tracking-[0.15em]">
                  {stat.label}
                </div>
                <div className="text-lg md:text-xl font-bold font-heading text-white mt-1">
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  )
}
